import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  HostListener,
  Inject,
  OnInit,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { MatInputModule } from '@angular/material/input';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef } from 'ag-grid-community';
import { Router, RouterModule } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { StationaryLogService } from '../../features/stationary-log/services/stationary-log.service';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { PaginationHelper } from '../../shared/services/pagination.service';
import { PermissionService } from '../../shared/services/permission.service';
import { ModuleIds } from '../../shared/permission-constants/permission-const';

@Component({
  selector: 'app-stationary-log',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    AgGridAngular,
    FormsModule,
  ],
  templateUrl: './stationary-log.component.html',
  styleUrl: './stationary-log.component.scss',
})
export class StationaryLogComponent implements OnInit {
  public viewPortSize: boolean = false;
  public loading: boolean = true;
  public error: any = null;
  readonly dialog = inject(MatDialog);
  columnDefs: any[] = [];
  public rowData : any[] = [];
  public defaultColDef: ColDef = {
    filter: 'agTextColumnFilter',
    floatingFilter: true,
  };
  public rowSelection: 'single' | 'multiple' = 'multiple';
  public paginationPageSize = 10;
  public paginationPageSizeSelector: number[] | boolean = [10, 25, 50];
  public themeClass: string = 'ag-theme-quartz';

  private stationaryLogService = inject(StationaryLogService);
  public stationaryLogData: any[] | null = null;

  searchTerm: string = '';
  private searchSubject = new Subject<string>();
  canCreateStationaryLog$ = this.permissionService.hasPermission(
    ModuleIds.StationaryLog,
    'Create'
  );
  canEditStationaryLog$ = this.permissionService.hasPermission(
    ModuleIds.StationaryLog,
    'Edit'
  );
  canEditStationaryLog:boolean = false;

  constructor(private router: Router, private datePipe: DatePipe, private permissionService: PermissionService) {}

  ngOnInit(): void {
    this.checkViewportSize();
    this.searchSubject
      .pipe(debounceTime(600), distinctUntilChanged())
      .subscribe((searchTerm) => {
        this.fetchStationaryLogs(searchTerm);
      });
    this.fetchStationaryLogs();
    this.canEditStationaryLog$.subscribe(val => {
      this.canEditStationaryLog = val;
    });

  }

  checkViewportSize() {
    if (window.innerWidth > 1200) {
      this.viewPortSize = true;
    } else {
      this.viewPortSize = false;
    }
    this.updateColumnDefs();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkViewportSize();
  }
  openEditDialog(itemData: any) {
    const dialogRef = this.dialog.open(EditStockDialogComponent, {
      data: {
        id: itemData.id,
        itemName: itemData.itemName,
        date: itemData.date,
        openingStock: itemData.openingStock,
        closingStock: itemData.closingStock,
      },
      disableClose: true,
    });
  
    dialogRef.afterClosed().subscribe((result) => {
      dialogRef.close();
      if (result) {
        this.fetchStationaryLogs();
      }
    });
  }
  onCellClicked(params: any) {
    if (params.column.colId === 'action' && params.event.target.dataset.action) {
      if (params.event.target.dataset.action === 'edit') {
        this.openEditDialog(params.data);
      }
    }
  }
  updateColumnDefs(): void {
    this.columnDefs = [
      {
        headerName: 'Item Name',
        field: 'itemName',
        flex: this.viewPortSize ? 1 : 0,
        valueFormatter: (params: any) => {
          if (!params.value) return '';
          return (
            params.value.charAt(0).toUpperCase() +
            params.value.slice(1).toLowerCase()
          );
        },
      },
      {
        headerName: 'Date',
        field: 'date',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Opening Stock',
        field: 'openingStock',
        flex: this.viewPortSize ? 1 : 0,
      },
      {
        headerName: 'Closing Stock',
        field: 'closingStock',
        flex: this.viewPortSize ? 1 : 0,
      },
    ];

    this.columnDefs.push({
        field: 'action',
        headerName: 'Actions',
        floatingFilter: false,
        filter: false,
        flex: this.viewPortSize ? 1 : 0,
        cellRenderer: (params: any) => {
          if (!params.data.isLatest) {
            return '';
          }
  
          const container = document.createElement('div');
          container.style.display = 'flex';
          container.style.alignItems = 'center';
  
          const editIcon = document.createElement('img');
          editIcon.src = 'assets/images/icons/icon-edit.svg';
          editIcon.style.cursor = 'pointer';
          editIcon.setAttribute('data-action', 'edit');
          if(this.canEditStationaryLog){
            container.appendChild(editIcon);
          }
  
          return container;
        },
    });
  }

  navigateTo(route: string) {
    this.router.navigate([route]);
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    const searchTerm = input.value;
    this.searchSubject.next(searchTerm);
  }

  fetchStationaryLogs(search = '') {
    this.stationaryLogService.fetchStationaryLogs(search).subscribe({
      next: (data) => {
        const latestDateMap = new Map<string, string>();

        data.forEach((log: any) => {
          const itemName = log.stationoryData?.stationoryName || '--';
          const date = log.date;

          if (
            !latestDateMap.has(itemName) ||
            new Date(date) > new Date(latestDateMap.get(itemName)!)
          ) {
            latestDateMap.set(itemName, date);
          }
        });

        this.rowData = data.map((stationaryLog: any) => {
          const itemName =
            stationaryLog?.stationoryData?.stationoryName || '--';
          const date = stationaryLog?.date;
          return {
            id: stationaryLog?.id,
            itemName,
            date: this.datePipe.transform(date, 'dd/MM/yyyy', '+0000') || '--',
            openingStock: stationaryLog?.openingStock ?? 0,
            closingStock: stationaryLog?.closingStock ?? 0,
            isLatest: date === latestDateMap.get(itemName),
          };
        });
        this.paginationPageSizeSelector = PaginationHelper.getPaginationPageSizeOptions(this.rowData);
      },
      error: (error) => {
        console.error('Error fetching stationaries:', error);
      },
    });
  }
}

@Component({
  selector: 'app-edit-stock',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MatDialogModule,
    MatDatepickerModule,
  ],
  templateUrl: './edit-stock-dialog.html',
  styleUrl: './edit-stock-dialog.scss',
  encapsulation: ViewEncapsulation.None,
})
export class EditStockDialogComponent implements OnInit {
  editStockForm!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<EditStockDialogComponent>,
    private stationaryLogService: StationaryLogService,
    private toastr: ToastrService,

    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}
  today: Date = new Date();
  ngOnInit(): void {    
    this.initForm();
  }
  dateFilter = (d: Date | null): boolean => {
    const currentDate = d || new Date();
    return currentDate <= this.today;
  };

  parseDate(dateStr: string): Date | null {
    if (!dateStr) return null;
  
    const parts = dateStr.split('/');
    if (parts.length !== 3) return null;
  
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
  
    return new Date(year, month, day);
  }

  validateStockInput(event: KeyboardEvent) {
    const char = event.key;
    if (!/^\d$/.test(char)) {
      event.preventDefault();
    }
  }

  initForm(): void {
    this.editStockForm = this.fb.group({
      id: [this.data.id],
      itemName: [{ value: this.data.itemName, disabled: true }, Validators.required],
      openingStock: [{ value: this.data.openingStock, disabled: true }, Validators.required],
      closingStock: [
        this.data.closingStock,
        [Validators.required, Validators.pattern('^[0-9]*$')] 
      ],
      date: [{value : this.parseDate(this.data.date), disabled: true}, Validators.required], 
    });
  }

  allowOnlyInteger(event: KeyboardEvent): void {
    const inputChar = event.key;
    if (!/^[0-9]$/.test(inputChar)) {
      event.preventDefault();
    }
  }
  
  blockInvalidPaste(event: ClipboardEvent): void {
    const pastedInput: string = event.clipboardData?.getData('text') || '';
    const regex = /^[1-9]\d*$/;
    if (!regex.test(pastedInput)) {
      event.preventDefault();
    }
  }

  onSubmit(): void {
    
    const openingStock = this.data.openingStock;
    const closingStock = parseInt(this.editStockForm.get('closingStock')?.value, 10) || 0;
  
    // if (closingStock > openingStock) {
    //   this.toastr.error('Closing stock cannot be greater than opening stock.');
    //   return;
    // }
  
    const updateData = {
      id: this.data.id,
      closingStock: closingStock,
    };
  

    console.log('Updating Stock with:', updateData);

    this.stationaryLogService.updateStationaryLog(updateData).subscribe({
      next: (response) => {
        console.log('Update Successful:', response);
        this.toastr.success('Stock updated successfully');
        this.dialogRef.close(true); 
      
      },
      error: (error) => {
        console.error('Update Error:', error);
        this.toastr.error(error.message);
      },
    });
  }
}
